import { useCallback, useEffect, useMemo, useState } from 'react'

import Loader from '@/common/components/Loader'
import { Icon } from '@/common/components/Icon'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import { useTableParams } from '@/core/hooks/table-params'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import {
  Staff,
  createStaff,
  deleteStaff,
  getStaff,
  singleStaff,
  updateStaff,
} from '@/core/services/staff'

import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { CreateStaffModal, IStaffFormValues } from './CreateStaffModal'
import { RemoveStaffModal } from './RemoveStaffModal'
import { STAFF_PERMISSION_OPTIONS, StaffPermission } from './types'

const PERMISSION_LABEL: Record<StaffPermission, string> = Object.fromEntries(
  STAFF_PERMISSION_OPTIONS.map((o) => [o.value, o.label]),
) as Record<StaffPermission, string>

const buildPhone = (phone: IStaffFormValues['phone']) =>
  `${phone.countryCodeValue ?? ''}${phone.phoneNumber ?? ''}`

export const StaffPage: React.FC = () => {
  const { hasEditPermission } = useCheckPermission({ page: 'STAFF' })
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
    searchParams,
  } = useTableParams({
    extraParams: {
      name: '',
      phone: '',
    },
  })

  const [staff, setStaff] = useState<Staff[]>([])
  const [loading, setLoading] = useState(false)
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<Staff | null>(null)
  const [bulkRemoveOpen, setBulkRemoveOpen] = useState(false)

  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const handleError = (e: unknown) => {
    if (e instanceof Error) {
      setIsError(true)
      setShowMessageBox(true)
      setMessage(e.message)
      setErrorStatus(
        (e as { response?: { status?: number } })?.response?.status,
      )
    } else {
      console.error(e)
    }
  }

  const getData = useCallback(async () => {
    try {
      setLoading(true)

      const params = {
        page,
        size,
        sortBy,
        sortDirection,
        name: searchParams.current.name || undefined,
        phone: searchParams.current.phone || undefined,
      }

      const { status, data } = await getStaff(params)
      if (status === 200) {
        setStaff(data.content)
        setTotalElemenets(data.totalElements)
        setSize(data.size)
        setPage(data.pageNumber + 1)
      }
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }, [page, size, setPage, setSize, setTotalElemenets])

  useEffect(() => {
    getData()
  }, [getData])

  const handleSelectAll = () => {
    if (!hasEditPermission) return
    const next = !isCheckAll
    setIsCheckAll(next)
    setIsCheck(next ? staff.map((s) => s.id) : [])
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) return
    setIsCheck((prev) =>
      checked ? [...prev, id] : prev.filter((item) => item !== id),
    )
  }

  const openCreate = () => {
    setEditing(null)
    setCreateOpen(true)
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const openEdit = async (row: Staff) => {
    if (!hasEditPermission) return
    try {
      setLoading(true)
      const { status, data } = await singleStaff(row.id)
      if (status === 200) {
        setEditing(data)
        setCreateOpen(true)
      }
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (values: IStaffFormValues, id?: string) => {
    const payload = {
      name: values.name,
      phone: buildPhone(values.phone),
      permissions: values.permissions,
    }
    try {
      setLoading(true)
      if (id) {
        await updateStaff(id, payload)
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Staff updated successfully')
      } else {
        await createStaff(payload)
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Staff created successfully')
      }
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteOne = async (id: string) => {
    try {
      setLoading(true)
      await deleteStaff(id)
      setIsCheck((prev) => prev.filter((c) => c !== id))
      setIsError(false)
      setShowMessageBox(true)
      setMessage('Delete Successful')
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleBulkDelete = async () => {
    try {
      setLoading(true)
      await Promise.all(isCheck.map((id) => deleteStaff(id)))
      setIsCheck([])
      setIsCheckAll(false)
      setIsError(false)
      setShowMessageBox(true)
      setMessage('Delete Successful')
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleSearchParams = useCallback(
    async (col: IColumn, q: string | null) => {
      searchParams.current = {
        ...searchParams.current,
        [col.accessor]: q,
      }

      setLoading(true)
      try {
        const params = {
          page: 0,
          size,
          sortBy,
          name: searchParams.current.name || undefined,
          phone: searchParams.current.phone || undefined,
        }

        const res = await getStaff(params)
        setStaff(res.data.content)
        setTotalElemenets(res.data.totalElements)
      } catch (err) {
        console.log(err)
      } finally {
        setLoading(false)
      }
    },
    [size, sortBy, setTotalElemenets],
  )

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: (
          <Checkbox
            disabled={!hasEditPermission}
            onCheckedChange={handleSelectAll}
            checked={isCheckAll}
          />
        ),
        accessor: '',
        render: (row: Staff) => (
          <Checkbox
            disabled={!hasEditPermission}
            onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
            checked={isCheck.includes(row.id)}
          />
        ),
      },
      {
        title: 'Name',
        accessor: 'name',
        sortable: true,
        searchable: true,
        render: (row: Staff) => (
          <span className="text-monochrome-1">{row.name}</span>
        ),
      },
      {
        title: 'Phone',
        accessor: 'phone',
        sortable: true,
        searchable: true,
        render: (row: Staff) => (
          <span className="text-monochrome-1">{row.phone}</span>
        ),
      },
      {
        title: 'Permissions',
        accessor: 'permissions',
        render: (row: Staff) => (
          <div className="flex flex-col gap-2">
            {row.permissions.map((p) => (
              <div key={p} className="flex items-center gap-2">
                <Checkbox checked={row.permissions.includes(p)} disabled />
                <span className="text-body-medium text-monochrome-2">
                  {PERMISSION_LABEL[p]}
                </span>
              </div>
            ))}
          </div>
        ),
      },
      {
        title: 'Edit',
        accessor: '',
        render: (row: Staff) => (
          <a
            className="view text-accent-1 underline"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              openEdit(row)
            }}
          >
            Edit
          </a>
        ),
      },
    ],
    [hasEditPermission, isCheck, isCheckAll, staff],
  )

  return (
    <>
      <Header
        pageTitle="Staff"
        actions={
          <>
            {hasEditPermission && isCheck.length >= 1 && (
              <Button
                variant="destructive"
                onClick={() => setBulkRemoveOpen(true)}
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
            {hasEditPermission && (
              <Button variant="primary-bordered" onClick={openCreate}>
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle icon="staff" title="Staff" className="mb-5" />
        <Table
          className="[&_th]:!whitespace-nowrap"
          isCheck={isCheck}
          columns={columns}
          data={staff}
          handleSortBy={handleSortBy}
          handleSearch={(col, query) => {
            handleSearchParams(col, query)
          }}
          searchParams={searchParams.current}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={setPage}
      />

      <CreateStaffModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        staff={editing}
        onSubmit={handleSubmit}
        onDelete={handleDeleteOne}
      />

      <RemoveStaffModal
        open={bulkRemoveOpen}
        onOpenChange={setBulkRemoveOpen}
        onConfirm={handleBulkDelete}
        staffName={
          isCheck.length === 1
            ? staff.find((s) => s.id === isCheck[0])?.name
            : `${isCheck.length} staff members`
        }
      />

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
